import {
  ChangeEvent,
  CSSProperties,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { ReactComponent as PenEdit } from "./../../../../assets/svg/icons/penEdit.svg";
import { ReactComponent as CloseIcon } from "./../../../../assets/svg/icons/close.svg";
import { ReactComponent as PlusIcon } from "./../../../../assets/svg/icons/plus.svg";
import { ReactComponent as TrashIcon } from "./../../../../assets/svg/icons/trash.svg";
import { ReactComponent as CheckIcon } from "./../../../../assets/svg/icons/check.svg";
import { ReactComponent as UndoIcon } from "./../../../../assets/svg/icons/undo.svg";
import { ReactComponent as RedoIcon } from "./../../../../assets/svg/icons/redo.svg";
import { ReactComponent as PositiveIcon } from "./../../../../assets/svg/icons/positive.svg";
import { ReactComponent as NegativeIcon } from "./../../../../assets/svg/icons/negative.svg";
import FarmMap from "../../../../assets/png/farmMap.png";
import { IKmlsUpload } from "../../../../@types/app/IKmlUpload";
import { ICoord, ICoords } from "../../../../@types/GoogleMaps/ICoord";
import { readFileKML } from "../../../../utils/kml/readFile";
import { ButtonWithChildren } from "../../../Buttons/ButtonWithChildren/Index";
import { AlertModal } from "../../../Modal/Alert/Index";
import { SimpleButtonClose } from "../../../Modal/Contents/SimpleButtonClose/Index";
import { TextModal } from "../../../Modal/Contents/TextModal/Index";
import { TitleModal } from "../../../Modal/Contents/TitleModal/Index";
import { Modal } from "../../../Modal/Default/Index";
import { StaticMapRender } from "../StaticMapRender/Index";
import { Polygon as GooglePolygon } from "@react-google-maps/api";
import { Map } from "../../../GoogleMaps/Map/Index";
import Constants from "../../../../constants/Index";
import { Controller, useFieldArray } from "react-hook-form";

type UploadFileKmlProps = {
  children: ReactNode;
  title: string;
  name: string;
  reset?: boolean;
  onMessageError?: (error: string) => void;
  onlyRead?: boolean;
  control: any;
  required?: boolean;
  errorText?: any;
};

const farmEditStyle = {
  strokeColor: "rgba(255, 149, 1, 1)",
  fillColor: "rgba(255, 149, 1, 0.5)",
  strokeOpacity: 1,
  fillOpacity: 0.5,
  strokeWeight: 3,
};

const contentStyleModal = {
  width: "95%",
  height: "95vh",
  maxWidth: "1350px",
  maxHeight: "937px",
  borderRadius: "20px",
  overflow: "hidden",
} as CSSProperties;

const contentStyleAlert = {
  width: "335px",
  height: "237px",
  borderRadius: "20px",
} as CSSProperties;

export function FileUploadKml({
  onMessageError,
  children,
  title,
  name,
  reset,
  onlyRead = false,
  control,
  required,
  errorText,
}: UploadFileKmlProps) {
  const { append, remove } = useFieldArray({
    control,
    name
  });
  const [fileName, setFileName] = useState<string>("");
  const [fileError, setFileError] = useState<string>("");
  const [farmEditPolygon, setFarmEditPolygon] = useState<IKmlsUpload>([]);
  const [farmPolygon, setFarmpolygon] = useState<IKmlsUpload>([]);
  const [editPolygon, setEditPolygon] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [polygonComponent, setPolygonComponent] = useState<google.maps.Polygon | null>(null);
  const [initialPolygon, setInitialPolygon] = useState<ICoords>([]);

  useEffect(() => {
    remove();
    if (farmPolygon[0]?.coords) append(farmPolygon[0]?.coords);
  }, [farmPolygon]);

  // Isso lida com a mensagem de erro
  function handleError(error: string) {
    if (onMessageError) onMessageError(error);
    else setFileError(error);
  }

  // Isso faz a leitura do arquivo
  async function handleFileCoords(event: ChangeEvent<HTMLInputElement>) {
    setFileError("");

    if (event.target.files) {
      const fileName = event.target.files[0].name;
      if (!fileName.includes(".kml")) {
        handleError("Por favor! Somente aquivos Kml/kmz");
        return;
      }
      const response = await readFileKML(event);
      // se a leitura devolver NULL mostramos um erro
      if (!response) {
        handleError(`Desculpe! mas não conseguimos ler o arquivo ${fileName}`);
        return;
      }
      const dataCoords = response as IKmlsUpload;
      const [coords] = dataCoords.map((data) => data.coords);
      // Se não tivermos no mínimo 3 coordenadas devolvemos um erro
      if (coords.length < 3) {
        handleError(`Desculpe! mas não conseguimos ler o arquivo ${fileName}`);
        return;
      }
      setFarmpolygon(() => response as IKmlsUpload);
      setFileName(fileName);
    }
  }

  // Isso redefine o input em caso de desistência
  function resetFileSelected() {
    setFarmpolygon([]);
    setFileName("");
    setEditPolygon(false);
  }

  // Funções para edição do polígono (baseado no DrawOnMap)
  const onLoadPolygon = (polygon: google.maps.Polygon) => setPolygonComponent(polygon);

  function onMouseUp(event: google.maps.MapMouseEvent) {
    const coords = polygonComponent?.getPath().getArray().map((latLng) => {
      return { lat: latLng.lat(), lng: latLng.lng() };
    }) as ICoords;
    if (!!coords) {
      const updatedPolygon = [{ name: fileName, coords }];
      setFarmEditPolygon(updatedPolygon);
    }
  }

  const onDiscardChanges = () => {
    setFarmEditPolygon(farmPolygon);
    setEditPolygon(false);
  };

  const onConfirmChanges = () => {
    setFarmpolygon(farmEditPolygon);
    setEditPolygon(false);
  };


  useEffect(() => {
    resetFileSelected();
  }, [reset]);

  // Inicializar polígono de edição quando modal abrir
  useEffect(() => {
    if (editPolygon && farmPolygon.length > 0) {
      setFarmEditPolygon(farmPolygon);
      setInitialPolygon(farmPolygon[0]?.coords || []);
    }
  }, [editPolygon, farmPolygon]);

  return (
    <>
      <Modal contentStyle={contentStyleModal} visible={editPolygon}>
        {farmEditPolygon.length > 0 && (
          <>
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <Map fitBounds={farmEditPolygon[0]?.coords}>
                <GooglePolygon
                  options={Constants.POLYGON_YELLOW}
                  onLoad={onLoadPolygon}
                  onMouseUp={onMouseUp}
                  path={farmEditPolygon[0]?.coords || []}
                  editable
                />
              </Map>
              
              <section className="fileUploadKml__header--modal" style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 1000 }}>
                <div className="uploadFileKMl__button--namekml">
                  <h6>{fileName}</h6>
                  <button onClick={resetFileSelected} type={"button"}>
                    <CloseIcon />
                  </button>
                </div>
                <button
                  onClick={() => setEditPolygon((prev) => !prev)}
                  type={"button"}
                  style={{ position: 'absolute', top: '0', right: '0' }}
                >
                  <CloseIcon />
                </button>
              </section>

              <section className="fileUploadKml__footer--modal" style={{ position: 'absolute', bottom: '20px', left: '20px', zIndex: 1000 }}>
                <div className="fileUploadKml__footer--modal-controlls">
                  <h6>Editar arquivo</h6>
                  <button type={"button"}>
                    <UndoIcon />
                  </button>
                  <button type={"button"}>
                    <RedoIcon />
                  </button>
                  <button className="cancelButton" type={"button"} onClick={onDiscardChanges}>
                    <TrashIcon />
                  </button>
                  <button
                    className="confirmButton"
                    onClick={onConfirmChanges}
                    type={"button"}
                  >
                    <CheckIcon />
                  </button>
                </div>
              </section>
            </div>
          </>
        )}
      </Modal>

      <AlertModal visible={modalVisible} contentStyle={contentStyleAlert}>
        <section className="modalEdit__alert">
          <SimpleButtonClose
            onClick={() => {
              setModalVisible(!modalVisible);
            }}
          />
          <TitleModal>Você tem certeza?</TitleModal>
          <TextModal>
            Ao remover, você perde todas as edições realizadas!
          </TextModal>
          <div className="buttons-controll">
            <ButtonWithChildren>
              <div className="buttonWithChildren--content-inline">
                <PositiveIcon />
                <h6>Sim</h6>
              </div>
            </ButtonWithChildren>
            <ButtonWithChildren>
              <div className="buttonWithChildren--content-inline">
                <NegativeIcon />
                <h6>Não</h6>
              </div>
            </ButtonWithChildren>
          </div>
        </section>
      </AlertModal>

      <div className="uploadFileKMl--container">
        {!onlyRead && <h6>{title}</h6>}
        {!onMessageError && <small>{fileError}</small>}

        <div className="uploadFileKMl--kml">
          {farmPolygon.length > 0 ? (
            <StaticMapRender
              mainPolygon={farmPolygon[0].coords}
              microAreas={[]}
              fitCoords={farmPolygon[0].coords}
            />
          ) : (
            <img src={FarmMap} alt="mapa fazenda" />
          )}
          {!onlyRead && farmPolygon.length > 0 && (
            <button
              className="uploadFileKMl--buttonEdit"
              onClick={() => {
                if (farmPolygon.length > 0) {
                  setFarmEditPolygon(() => farmPolygon);
                  setEditPolygon((prev) => !prev);
                }
              }}
              type={"button"}
            >
              {farmPolygon.length > 0 ? (
                <PenEdit fill="#FFF" />
              ) : (
                <PlusIcon fill="#FFF" />
              )}
            </button>
          )}
        </div>
        <Controller
          name={name}
          control={control}
          render={({ field }) =>
            fileName ? (
              <div className="uploadFileKMl__button--namekml">
                <h6>{fileName}</h6>
                {!onlyRead && (
                  <button onClick={resetFileSelected} type={"button"}>
                    <CloseIcon />
                  </button>
                )}
              </div>
            ) : (
              <div className="uploadFileKMl__container">
                <>
                  {!onlyRead && (
                    <label htmlFor="inputKMLfile">{children}</label>
                  )}

                  <input
                    type="file"
                    id="inputKMLfile"
                    onChange={(event) => {
                      handleFileCoords(event);
                      field.onChange(event);
                    }}
                  />
                </>
              </div>
            )
          }
        />
        {errorText && (
          <small className="textInput--small-error animation__fadeIn">
            {errorText}
          </small>
        )}
      </div>
    </>
  );
}
